'use client';

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Typography } from '../../../components';
import BillsTable from './components/BillsTable';
import FiltersSection from './components/FiltersSection';
import PayBillModal from './components/PayBillModal';
import { DEFAULT_FILTERS, DEFAULT_PAGE_SIZE, SEARCH_DEBOUNCE_DELAY } from './constants';
import { BillsFilters, RevshareBill, AvailableModel } from './types';
import { usePageBreadcrumbs } from '../../../hooks/usePageBreadcrumbs';
import { useTranslations } from '../../../contexts/translation.context';
import { showError, showCreatorSuccess } from '../../../utils/toast';
import { parseApiError } from '../../../utils/errorParser';
import clientRevshareService from '../../../api/client/clientRevshareService';
import clientAccService from '../../../api/client/clientAccService';

// Stats data from API - RefShareCostStats
interface BillsStats {
  total_amount: number;
  paid: number;
  in_pending: number;
}

// Helper function to get date range based on filter
const getDateRange = (dateRange: string, customStartDate?: string, customEndDate?: string) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  // If custom dates are provided, use them
  if (dateRange === 'custom' && customStartDate && customEndDate) {
    return {
      startDate: customStartDate,
      endDate: customEndDate,
    };
  }

  switch (dateRange) {
    case 'today':
      return {
        startDate: today.toISOString().split('T')[0],
        endDate: today.toISOString().split('T')[0],
      };
    case 'week':
      const weekStart = new Date(today);
      weekStart.setDate(today.getDate() - 7);
      return {
        startDate: weekStart.toISOString().split('T')[0],
        endDate: today.toISOString().split('T')[0],
      };
    case 'month':
      const monthStart = new Date(today);
      monthStart.setDate(today.getDate() - 30);
      return {
        startDate: monthStart.toISOString().split('T')[0],
        endDate: today.toISOString().split('T')[0],
      };
    case 'quarter':
      const quarterStart = new Date(today);
      quarterStart.setDate(today.getDate() - 90);
      return {
        startDate: quarterStart.toISOString().split('T')[0],
        endDate: today.toISOString().split('T')[0],
      };
    case 'halfyear':
      const halfYearStart = new Date(today);
      halfYearStart.setDate(today.getDate() - 180);
      return {
        startDate: halfYearStart.toISOString().split('T')[0],
        endDate: today.toISOString().split('T')[0],
      };
    case 'year':
      const yearStart = new Date(today);
      yearStart.setDate(today.getDate() - 365);
      return {
        startDate: yearStart.toISOString().split('T')[0],
        endDate: today.toISOString().split('T')[0],
      };
    case 'all':
      return {
        startDate: null,
        endDate: null,
      };
    default:
      return {
        startDate: null,
        endDate: null,
      };
  }
};

const RevshareBillsPage: React.FC = () => {
  const { t } = useTranslations();
  const [bills, setBills] = useState<RevshareBill[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalCount, setTotalCount] = useState(0);

  // Stats from API
  const [stats, setStats] = useState<BillsStats>({
    total_amount: 0,
    paid: 0,
    in_pending: 0,
  });

  // Filters state
  const [filters, setFilters] = useState<BillsFilters>(DEFAULT_FILTERS);
  const [localSearch, setLocalSearch] = useState('');
  const prevSearchRef = useRef(localSearch);
  const [availableModels, setAvailableModels] = useState<AvailableModel[]>([]);
  const [modelsLoading, setModelsLoading] = useState(false);

  // Modal state
  const [isPayModalOpen, setIsPayModalOpen] = useState(false);
  const [selectedBill, setSelectedBill] = useState<RevshareBill | null>(null);
  const [isPayingBill, setIsPayingBill] = useState(false);

  const breadcrumbs = useMemo(
    () => [
      { label: t('client.revshare.bills.breadcrumbs.home'), href: '/client/dashboard' },
      { label: t('client.revshare.bills.breadcrumbs.revshare'), href: '/client/revshare/offers' },
      { label: t('client.revshare.bills.breadcrumbs.bills'), isActive: true },
    ],
    [t]
  );
  usePageBreadcrumbs(breadcrumbs);

  // Check if there are any active filters
  const hasActiveFilters = useMemo(() => {
    return (
      filters.search !== '' ||
      filters.amount !== null ||
      filters.client_id !== null ||
      filters.company_id !== null ||
      filters.inner_company_id !== null ||
      filters.partner_id !== null ||
      filters.model_id !== null ||
      filters.status !== 'all' ||
      filters.dateRange !== 'all' ||
      (filters.customStartDate !== undefined && filters.customStartDate !== null) ||
      (filters.customEndDate !== undefined && filters.customEndDate !== null)
    );
  }, [filters]);

  // Load available models for filter
  const fetchAvailableModels = useCallback(async (search: string = '') => {
    setModelsLoading(true);
    try {
      const response = await clientAccService.getAccounts({
        page: 1,
        page_size: 50,
        search: search,
      });
      if (response.data && response.data.objects) {
        setAvailableModels(
          response.data.objects.map((acc: any) => ({
            id: acc._id || acc.id,
            name: acc.name,
            username: acc.of_user_name || acc.username,
            avatar: acc.avatar,
          }))
        );
      }
    } catch (error) {
      console.error('Error loading models:', error);
    } finally {
      setModelsLoading(false);
    }
  }, []);

  // Load models on mount
  useEffect(() => {
    fetchAvailableModels();
  }, [fetchAvailableModels]);

  const handleModelSearchChange = useCallback(
    (value: string) => {
      fetchAvailableModels(value);
    },
    [fetchAvailableModels]
  );

  const loadBills = useCallback(async () => {
    setIsLoading(true);
    try {
      const params: any = {
        page: currentPage,
        page_size: pageSize,
      };

      // Search parameter
      if (filters.search) params.search = filters.search;

      // Status filter (now supported by API)
      if (filters.status && filters.status !== 'all') {
        params.status = filters.status;
      }

      // API parameters
      if (filters.amount !== null) params.amount = filters.amount;
      if (filters.client_id !== null) params.client_id = filters.client_id;
      if (filters.company_id !== null) params.company_id = filters.company_id;
      if (filters.inner_company_id !== null) params.inner_company_id = filters.inner_company_id;
      if (filters.partner_id !== null) params.partner_id = filters.partner_id;
      if (filters.model_id !== null) params.model_id = filters.model_id;

      // Date range parameters (using double underscore for backend)
      const dateRange = getDateRange(
        filters.dateRange,
        filters.customStartDate || undefined,
        filters.customEndDate || undefined
      );
      if (dateRange.startDate) params.created_at__start = dateRange.startDate;
      if (dateRange.endDate) params.created_at__end = dateRange.endDate;

      const response = await clientRevshareService.getBills(params);

      let billsData: RevshareBill[] = [];
      if (response && response.objects && Array.isArray(response.objects)) {
        billsData = response.objects;
      }

      setBills(billsData);
      setTotalCount(response?.count || billsData.length);
    } catch (error) {
      console.error('Error loading revshare bills:', error);
      showError(parseApiError(error, t('client.revshare.bills.messages.loadError')));
      setBills([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize, filters, t]);

  const loadStats = useCallback(async () => {
    try {
      const response = await clientRevshareService.getBillsStats();
      if (response) {
        setStats({
          total_amount: parseFloat(response.total_amount) || 0,
          paid: parseFloat(response.paid) || 0,
          in_pending: parseFloat(response.in_pending) || 0,
        });
      }
    } catch (error) {
      console.error('Error loading bills stats:', error);
    }
  }, []);

  // Debounce search
  useEffect(() => {
    // Пропускаем если значение не изменилось (включая первый рендер)
    if (prevSearchRef.current === localSearch) return;

    const timeoutId = setTimeout(() => {
      prevSearchRef.current = localSearch;
      setFilters((prev) => ({ ...prev, search: localSearch }));
      setCurrentPage(1);
    }, SEARCH_DEBOUNCE_DELAY);

    return () => clearTimeout(timeoutId);
  }, [localSearch]);

  useEffect(() => {
    loadBills();
  }, [loadBills]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  }, []);

  const handleFilterChange = useCallback(
    (key: keyof BillsFilters, value: string | string[] | number | null) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
      setCurrentPage(1);
    },
    []
  );

  const handleResetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    setLocalSearch('');
    setCurrentPage(1);
  }, []);

  // Open modal to confirm payment
  const handlePayBill = useCallback(
    (billId: string) => {
      const bill = bills.find((b) => b._id === billId);
      if (bill) {
        setSelectedBill(bill);
        setIsPayModalOpen(true);
      }
    },
    [bills]
  );

  // Close modal
  const handleClosePayModal = useCallback(() => {
    setIsPayModalOpen(false);
    setSelectedBill(null);
  }, []);

  // Confirm payment from modal
  const handleConfirmPayment = useCallback(async () => {
    if (!selectedBill) return;

    setIsPayingBill(true);
    try {
      await clientRevshareService.payBill(selectedBill._id);
      showCreatorSuccess(t('client.revshare.bills.messages.paySuccess'));
      handleClosePayModal();
      // Reload bills and stats after payment
      loadBills();
      loadStats();
    } catch (error) {
      console.error('Error paying bill:', error);
      showError(parseApiError(error, t('client.revshare.bills.messages.payError')));
    } finally {
      setIsPayingBill(false);
    }
  }, [selectedBill, loadBills, loadStats, t, handleClosePayModal]);

  return (
    <div className="flex flex-col h-full bg-white">
      <div className="px-4 py-6 md:py-4 md:px-6">
        <Typography variant="h5" className="md:font-semibold text-gray-900">
          {t('client.revshare.bills.title')}
        </Typography>
      </div>

      {/* Stats Cards - 3 cards */}
      <div className="px-4 pb-6 md:px-6 md:pb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 md:gap-4 max-md:border border-tin max-md:rounded-3">
          {/* Total Revenue from Revshare campaigns */}
          <div className="md:bg-white max-md:border-b md:border border-tin md:rounded-3 p-4">
            <Typography variant="body3" className="text-auth-muted mb-2">
              {t('client.revshare.bills.stats.totalRevenue')}
            </Typography>
            <Typography variant="h4" className="md:font-semibold text-gray-900">
              ${stats.total_amount.toFixed(2)}
            </Typography>
          </div>

          {/* Paid Invoices */}
          <div className="md:bg-white max-md:border-b md:border border-tin md:rounded-3 p-4">
            <Typography variant="body3" className="text-auth-muted mb-2">
              {t('client.revshare.bills.stats.paidInvoices')}
            </Typography>
            <Typography variant="h4" className="md:font-semibold text-gray-900">
              ${stats.paid.toFixed(2)}
            </Typography>
          </div>

          {/* Next Payment */}
          <div className="md:bg-white md:border border-tin md:rounded-3 p-4">
            <Typography variant="body3" className="text-auth-muted mb-2">
              {t('client.revshare.bills.stats.nextPayment')}
            </Typography>
            <Typography variant="h4" className="md:font-semibold text-gray-900">
              ${stats.in_pending.toFixed(2)}
            </Typography>
          </div>
        </div>
      </div>

      <FiltersSection
        filters={filters}
        localSearch={localSearch}
        hasActiveFilters={hasActiveFilters}
        availableModels={availableModels}
        modelsLoading={modelsLoading}
        onModelSearchChange={handleModelSearchChange}
        onSearchChange={setLocalSearch}
        onFilterChange={handleFilterChange}
        onResetFilters={handleResetFilters}
      />

      <div className="px-4 md:px-6 md:h-full">
        <BillsTable
          bills={bills}
          isLoading={isLoading}
          totalCount={totalCount}
          currentPage={currentPage}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onPayBill={handlePayBill}
        />
      </div>

      {/* Pay Bill Confirmation Modal */}
      <PayBillModal
        isOpen={isPayModalOpen}
        bill={selectedBill}
        onClose={handleClosePayModal}
        onConfirm={handleConfirmPayment}
        isLoading={isPayingBill}
      />
    </div>
  );
};

export default RevshareBillsPage;
